<template>
  <e-dialog ref="dialogRef" @open="handleDialogOpen" @after-leave="handleDialogAfterLeave">
    <template #title>
      <div class="caption">{{ isEdit ? `#${editedRestriction?.number}` : 'Новое ограничение' }}</div>
    </template>

    <period-overlap-conflict-alert v-model="form.hasPeriodOverlapConflict" class="mb-6" />

    <e-form v-model="form.isValid" @submit="handleSubmit">
      <div class="d-flex align-start ga-4">
        <e-date-picker
          v-model="form.period.from"
          label="Период с"
          :min="periodFromMin"
          :max="form.period.to"
          :disabled="fromInThePast"
          :error-messages="periodFromErrorMessage"
        />
        <e-date-picker
          v-model="form.period.to"
          label="Период по"
          :min="dayjs.max(form.period.from, dayjs(new Date()))"
          :max="periodToMax"
          :error-messages="periodToErrorMessage"
        />
      </div>

      <div class="paragraph-l font-600 mt-6">Настройка ограничения</div>
      <div class="paragraph-s text-secondary mt-1 mb-6">Настройки должны отличаться от настроек по умолчанию</div>

      <div class="d-flex ga-4">
        <e-time-picker 
          v-model="form.checkInTime" 
          label="Расчётный час заезда" 
          :error-messages="checkInErrorMessage" 
          :rules="[required()]"
          @update:model-value="recalculateOrderByCheckInTime"
        />
        <e-time-picker
          v-model="form.checkOutTime"
          label="Расчетный час выезда"
          :error-messages="checkOutErrorMessage"
          :rules="[required()]"
          @update:model-value="recalculateOrderByCheckInTime"
        />
      </div>
      <e-checkbox v-model="form.nextDayCheckout" label="Выезд на следующие сутки" class="mt-4" disabled />

      <div class="paragraph-l font-600 mt-6">Группы услуг</div>
      <div class="paragraph-s text-secondary mt-1 mb-6">Расчетный час заезда определяет порядок оказания услуг</div>

      <e-data-items
        v-if="serviceGroups.length"
        :items="serviceGroups"
        :headers="serviceGroupHeaders"
        class="mt-4 mb-6"
      >
        <template v-slot:[`item.name`]="{ value }">
          <div>{{ value[0]?.value || '-' }}</div>
        </template>
        <template v-slot:[`item.services`]="{ value }">
          <div>
            <div v-for="(service, index) in value" :key="index" class="service-item">
              <span>{{ service.shortName }}</span>
              <img 
                v-if="service.isFirst" 
                :src="firstServiceIcon"
                alt="Первая услуга"
                title="Первая услуга"
              />
              <img 
                v-if="service.isLast" 
                :src="lastServiceIcon"
                alt="Последняя услуга"
                title="Последняя услуга"
              />
            </div>
          </div>
        </template>
      </e-data-items>

      <div class="paragraph-xs text-secondary mt-4 d-flex align-center">
        <div class="d-flex align-center ga-1">
          <img 
            :src="firstServiceIcon"
            alt="Первая услуга"
            title="Первая услуга"
          />
          <span>Первая оказываемая услуга</span>
        </div>
        <div class="d-flex align-center ga-1 ml-4">
          <img 
            :src="lastServiceIcon"
            alt="Последняя услуга"
            title="Последняя услуга"
          />
          <span>Последняя оказываемая услуга</span>
        </div>
      </div>

      <div class="d-flex align-center mt-6">
        <remove-restriction-dialog
          v-if="isEdit"
          :number="editedRestriction?.number"
          :action="removeRestrictionAction"
          :operation-delete="operationDelete"
          with-activator
        />
        <e-btn variant="secondary" class="mr-4 ml-auto" @click="handleCancel">Отмена</e-btn>
        <e-btn variant="primary" type="submit" :disabled="isSaveButtonDisabled" :loading="form.isLoading">
          {{ isEdit ? 'Сохранить' : 'Создать' }}
        </e-btn>
      </div>
    </e-form>
  </e-dialog>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, reactive, ref } from 'vue';
import { useRoute } from 'vue-router';
import type { ChessDateCellType } from '@/core/components/interface/Chess/Chess.type';
import type { CheckInOutRestrictionsTableRestrictionType } from '@/parts/tariffs/store/restrictions';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EBtn, ECheckbox, EDatePicker, EForm, ETimePicker } from '@/core/components/ui';
import EDataItems from '@/core/components/common/data-items/EDataItems.vue';
import type { DataTableHeaders } from '@/core/types/components/table';
import { useSnackbar } from '@/core/composables/useSnackbar';
import { getArrayMinMax } from '@/core/utils/array';
import {
  getDateWithUtcOffset,
  getIsoDateFormat,
  getIsoDateWithUtcOffset,
  getNormalizedPTHTime,
  getTimeFromPTH,
  isDayBefore,
} from '@/core/utils/date';
import { OperationStatus, type RangeOfDateOnly } from '@/graphql/generated';
import { errorTypes } from '@/parts/errors/utils/error-types';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import PeriodOverlapConflictAlert from '@/parts/tariffs/components/PricesAndRestrictions/PeriodOverlapConflictAlert.vue';
import RemoveRestrictionDialog from '@/parts/tariffs/components/PricesAndRestrictions/RemoveRestrictionDialog.vue';
import { useRestrictionsStore } from '@/parts/tariffs/store/restrictions';
import { useServiceGroupsStore } from '@/parts/tariffs/store/serviceGroups';
import type { TariffServiceGroupListItem } from '@/parts/tariffs/store/serviceGroups';
import { required } from '@/core/utils/validation';
import firstServiceIcon from '@/assets/img/pages/tariffs/service-groups/first-service.svg';
import lastServiceIcon from '@/assets/img/pages/tariffs/service-groups/last-service.svg';

const {
  checkInOutRestrictions,
  checkInOutRestrictionsChessPeriod,
  getCheckInOutRestrictions,
  addCheckInOutRestriction,
  updateCheckInOutRestriction,
  removeCheckInOutRestriction,
} = useRestrictionsStore();

const { 
  getTariffServiceGroups,
  tariffServiceGroups,
  getTariffScheduleServiceGroupsForRestriction,
  calculateTariffScheduleServiceGroups,
} = useServiceGroupsStore();

const props = defineProps<{
  selectedCells: ChessDateCellType[];
  operationDelete: OperationStatus | null | undefined;
}>();

const $route = useRoute();
const { showSnackbar } = useSnackbar();
const { timeZoneOffsetMin } = useAccommodationObjectsStore();

const dialogRef = ref();

const serviceGroups = ref<TariffServiceGroupListItem[]>([]);

const serviceGroupHeaders: DataTableHeaders = [
  {
    key: 'name[0].value',
    title: 'Наименование',
  },
  {
    key: 'services',
    title: 'Включённые услуги',
  },
];

const form = reactive<{
  period: RangeOfDateOnly;
  checkInTime: string;
  checkOutTime: string;
  nextDayCheckout: boolean;

  isValid: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  hasPeriodOverlapConflict: boolean;
}>({
  period: { from: '', to: '' },
  checkInTime: '',
  checkOutTime: '',
  nextDayCheckout: false,

  isValid: false,
  isLoading: false,
  isSuccess: false,
  hasPeriodOverlapConflict: false,
});

const fromInThePast = computed(() => form.period.from && isDayBefore(form.period.from, new Date()));

const isEdit = ref(false);
const editedRestriction = ref<CheckInOutRestrictionsTableRestrictionType>();

const tariffScheduleCid = computed(() => String($route.params.t_id));

const periodFromMin = computed(() => {
  const todayWithTimeZoneOffset = getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin.value);
  if (checkInOutRestrictionsChessPeriod.value.from > todayWithTimeZoneOffset) {
    return getDateWithUtcOffset(checkInOutRestrictionsChessPeriod.value.from, timeZoneOffsetMin.value);
  }
  if (editedRestriction.value && editedRestriction.value.period.from < todayWithTimeZoneOffset) {
    return getDateWithUtcOffset(editedRestriction.value.period.from, timeZoneOffsetMin.value);
  }
  return getDateWithUtcOffset(todayWithTimeZoneOffset, timeZoneOffsetMin.value);
});

const periodToMax = computed(() => {
  return getDateWithUtcOffset(checkInOutRestrictionsChessPeriod.value.to, timeZoneOffsetMin.value);
});

const defaultOptions = computed(() => checkInOutRestrictions.value?.defaults.defaultOptions);

let previousCheckInTime = '';
let previousCheckOutTime = '';

const recalculateOrderByCheckInTime = async () => {
  if (!form.checkInTime || !form.checkOutTime) return;
  
  if (checkInErrorMessage.value || checkOutErrorMessage.value) return;
  
  const isCheckInChanged = previousCheckInTime !== form.checkInTime;
  const isCheckOutChanged = previousCheckOutTime !== form.checkOutTime;
  
  if (!isCheckInChanged && !isCheckOutChanged) {
    return; 
  }
  
  previousCheckInTime = form.checkInTime;
  previousCheckOutTime = form.checkOutTime;
  
  const result = await calculateTariffScheduleServiceGroups({
    tariffScheduleCid: tariffScheduleCid.value,
    checkInOutSettings: {
      checkInTime: form.checkInTime,
      checkOutTime: form.checkOutTime,
      nextDayCheckout: form.nextDayCheckout,
    },
  });
  
  const groups = result?.data?.calculateTariffScheduleServiceGroups?.serviceGroups || [];
  serviceGroups.value = groups;
};

const isSaveButtonDisabled = computed(() => {
  if (!form.isValid) {
    return true;
  }

  if (
    form.checkInTime === getNormalizedPTHTime(defaultOptions.value?.checkIn) &&
    form.checkOutTime === getNormalizedPTHTime(defaultOptions.value?.checkOut)
  ) {
    return true;
  }

  if (
    editedRestriction.value &&
    form.checkInTime === getNormalizedPTHTime(editedRestriction.value.options.checkIn) &&
    form.checkOutTime === getNormalizedPTHTime(editedRestriction.value.options.checkOut) &&
    getIsoDateFormat(form.period.from) === editedRestriction.value.period.from &&
    getIsoDateFormat(form.period.to) === editedRestriction.value.period.to
  ) {
    return true;
  }

  return false;
});

const periodFromErrorMessage = computed(() => {
  const periodFrom = dayjs(form.period.from).format('YYYY-MM-DD');
  const periodTo = dayjs(form.period.to).format('YYYY-MM-DD');

  if (
    checkInOutRestrictions.value?.table.some((item) => {
      return (
        item.restriction.period.to >= periodFrom &&
        item.restriction.period.to <= periodTo &&
        item.restriction.restrictionId !== editedRestriction.value?.restrictionId &&
        !form.isLoading &&
        !form.isSuccess
      );
    })
  ) {
    return 'Наложение дат с существующим ограничением';
  }

  return '';
});

const periodToErrorMessage = computed(() => {
  const periodFrom = dayjs(form.period.from).format('YYYY-MM-DD');
  const periodTo = dayjs(form.period.to).format('YYYY-MM-DD');

  if (
    checkInOutRestrictions.value?.table.some((item) => {
      return (
        item.restriction.period.from >= periodFrom &&
        item.restriction.period.from <= periodTo &&
        item.restriction.restrictionId !== editedRestriction.value?.restrictionId &&
        !form.isLoading &&
        !form.isSuccess
      );
    })
  ) {
    return 'Наложение дат с существующим ограничением';
  }

  return '';
});

const checkInErrorMessage = computed(() => {
  if (!form.checkInTime || !form.checkOutTime) {
    return '';
  }

  const checkInTime = getTimeFromPTH(form.checkInTime);
  const checkOutTime = getTimeFromPTH(form.checkOutTime);

  if (!form.nextDayCheckout) {
    if (checkInTime === checkOutTime) {
      return 'Значения не могут совпадать';
    }
    if (checkInTime && checkOutTime && checkInTime > checkOutTime) {
      return 'Выезд не может быть раньше заезда';
    }
  }

  return '';
});

const checkOutErrorMessage = computed(() => {
  if (!form.checkInTime || !form.checkOutTime) {
    return '';
  }

  const checkInTime = getTimeFromPTH(form.checkInTime);
  const checkOutTime = getTimeFromPTH(form.checkOutTime);

  if (!form.nextDayCheckout) {
    if (checkInTime === checkOutTime) {
      return 'Значения не могут совпадать';
    }
    if (checkInTime && checkOutTime && checkInTime > checkOutTime) {
      return 'Выезд не может быть раньше заезда';
    }
  }

  return '';
});

const handleDialogOpen = async () => {
  if (props.selectedCells.length) {
    const editedRestrictionId = props.selectedCells.find((item) => item.id)?.id;

    if (editedRestrictionId) {
      editedRestriction.value = checkInOutRestrictions.value?.table.find(
        (item) => item.restriction.restrictionId === Number(editedRestrictionId),
      )?.restriction;
      form.period = {
        from: getDateWithUtcOffset(editedRestriction.value?.period.from, timeZoneOffsetMin.value),
        to: getDateWithUtcOffset(editedRestriction.value?.period.to, timeZoneOffsetMin.value),
      };
      isEdit.value = true;
    } else {
      const periodFromTo = getArrayMinMax(props.selectedCells.map((item) => item.date));
      form.period = {
        from: getDateWithUtcOffset(periodFromTo[0], timeZoneOffsetMin.value),
        to: getDateWithUtcOffset(periodFromTo[1], timeZoneOffsetMin.value),
      };
    }
  } else {
    form.period = {
      from: getDateWithUtcOffset(periodFromMin.value, timeZoneOffsetMin.value),
      to: getDateWithUtcOffset(periodToMax.value, timeZoneOffsetMin.value),
    };
  }

  if (editedRestriction.value) {
    form.checkInTime = getNormalizedPTHTime(editedRestriction.value.options.checkIn) ?? '';
    form.checkOutTime = getNormalizedPTHTime(editedRestriction.value.options.checkOut) ?? '';
    form.nextDayCheckout = editedRestriction.value.options.nextDayCheckout;

    previousCheckInTime = form.checkInTime;
    previousCheckOutTime = form.checkOutTime;
    
    const result = await getTariffScheduleServiceGroupsForRestriction({
      tariffScheduleCid: tariffScheduleCid.value,
      checkInOutRestrictionId: Number(editedRestriction.value.restrictionId),
    });
    serviceGroups.value = result?.data?.tariffScheduleServiceGroupsForRestriction?.serviceGroups || [];
    
  } else {
    form.checkInTime = getNormalizedPTHTime(defaultOptions.value?.checkIn) ?? '';
    form.checkOutTime = getNormalizedPTHTime(defaultOptions.value?.checkOut) ?? '';
    form.nextDayCheckout = defaultOptions.value?.nextDayCheckout || false;

    previousCheckInTime = form.checkInTime;
    previousCheckOutTime = form.checkOutTime;

    await getTariffServiceGroups();
    const groups = tariffServiceGroups.value?.serviceGroups || [];
    serviceGroups.value = groups;
  }
};

const handleDialogAfterLeave = () => {
  form.period = { from: '', to: '' };
  form.checkInTime = '';
  form.checkOutTime = '';
  form.nextDayCheckout = false;
  form.isSuccess = false;
  isEdit.value = false;
  editedRestriction.value = undefined;
  serviceGroups.value = [];
  if (form.hasPeriodOverlapConflict) {
    getCheckInOutRestrictions(tariffScheduleCid.value);
    form.hasPeriodOverlapConflict = false;
  }
};

const handleSubmit = async () => {
  if (!form.isValid) return;

  const checkInOutSettings = {
    checkInTime: form.checkInTime,
    checkOutTime: form.checkOutTime,
    nextDayCheckout: form.nextDayCheckout,
  };

  const period = {
    from: dayjs(form.period.from).format('YYYY-MM-DD'),
    to: dayjs(form.period.to).format('YYYY-MM-DD'),
  };

  form.isLoading = true;

  if (editedRestriction.value) {
    const result = await updateCheckInOutRestriction({
      period,
      restrictionId: editedRestriction.value.restrictionId,
      checkInOutSettings,
      tariffScheduleCid: tariffScheduleCid.value,
    });

    if (!result?.errors) {
      form.isSuccess = true;
      dialogRef.value.close();

      showSnackbar({ type: 'success', title: 'Ограничение РЧ изменено' });
    }
  } else {
    const result = await addCheckInOutRestriction({
      period,
      checkInOutSettings,
      tariffScheduleCid: tariffScheduleCid.value,
    });

    if (!result?.errors) {
      form.isSuccess = true;
      dialogRef.value.close();

      showSnackbar({ type: 'success', title: 'Ограничение РЧ создано' });
    } else if (result.errors[0].__typename === errorTypes.RestrictionPeriodOverlapConflictError) {
      form.hasPeriodOverlapConflict = true;
    }
  }

  form.isLoading = false;
};

const removeRestrictionAction = async () => {
  if (!editedRestriction.value) return;

  await removeCheckInOutRestriction({
    restrictionId: editedRestriction.value.restrictionId,
    tariffScheduleCid: tariffScheduleCid.value,
  });
  dialogRef.value.close();
};

const handleCancel = () => {
  dialogRef.value.close();
};
</script>

<style scoped lang="scss">
.service-item {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
